/**
 * Faceted Taxonomy Constants
 *
 * Defines the structured categorization system for recipes.
 * Used for filtering, routing, and admin form options.
 */

import type {
  RecipeCategory,
  RecipeCuisine,
  CookingMethod,
  DietaryTag,
  RecipeAttribute,
} from '../interfaces/IRecipe';

// =============================================================================
// CATEGORIES (required, single-select)
// =============================================================================

export const CATEGORIES: RecipeCategory[] = [
  'Breakfast',
  'Brunch',
  'Lunch',
  'Dinner',
  'Appetizer',
  'Side Dish',
  'Soup',
  'Salad',
  'Dessert',
  'Snack',
  'Beverage',
  'Bread',
  'Sauce',
  'Main Course',
];

export const CATEGORY_LABELS: Record<RecipeCategory, string> = {
  'Breakfast': 'Breakfast',
  'Brunch': 'Brunch',
  'Lunch': 'Lunch',
  'Dinner': 'Dinner',
  'Appetizer': 'Appetizer',
  'Side Dish': 'Side Dish',
  'Soup': 'Soup',
  'Salad': 'Salad',
  'Dessert': 'Dessert',
  'Snack': 'Snack',
  'Beverage': 'Beverage',
  'Bread': 'Bread',
  'Sauce': 'Sauce',
  'Main Course': 'Main Course',
};

// =============================================================================
// CUISINES (optional, single-select)
// =============================================================================

export const CUISINES: RecipeCuisine[] = [
  'American',
  'Italian',
  'Mexican',
  'Asian',
  'Chinese',
  'Japanese',
  'Indian',
  'Mediterranean',
  'French',
  'Greek',
  'Thai',
  'Korean',
  'Vietnamese',
  'Middle Eastern',
  'Southern',
  'Cajun',
  'Caribbean',
  'Puerto Rican',
  'Cuban',
  'Brazilian',
  'Spanish',
  'Filipino',
  'Other',
];

export const CUISINE_LABELS: Record<RecipeCuisine, string> = {
  'American': 'American',
  'Italian': 'Italian',
  'Mexican': 'Mexican',
  'Asian': 'Asian',
  'Chinese': 'Chinese',
  'Japanese': 'Japanese',
  'Indian': 'Indian',
  'Mediterranean': 'Mediterranean',
  'French': 'French',
  'Greek': 'Greek',
  'Thai': 'Thai',
  'Korean': 'Korean',
  'Vietnamese': 'Vietnamese',
  'Middle Eastern': 'Middle Eastern',
  'Southern': 'Southern',
  'Cajun': 'Cajun',
  'Caribbean': 'Caribbean',
  'Puerto Rican': 'Puerto Rican',
  'Cuban': 'Cuban',
  'Brazilian': 'Brazilian',
  'Spanish': 'Spanish',
  'Filipino': 'Filipino',
  'Other': 'Other',
};

// =============================================================================
// COOKING METHODS (optional, multi-select)
// =============================================================================

export const COOKING_METHODS: CookingMethod[] = [
  'Air Fryer',
  'Crock Pot',
  'Instant Pot',
  'Oven',
  'Stovetop',
  'Grill',
  'No-Cook',
  'Smoker',
];

export const COOKING_METHOD_LABELS: Record<CookingMethod, string> = {
  'Air Fryer': 'Air Fryer',
  'Crock Pot': 'Crock Pot / Slow Cooker',
  'Instant Pot': 'Instant Pot / Pressure Cooker',
  'Oven': 'Oven',
  'Stovetop': 'Stovetop',
  'Grill': 'Grill',
  'No-Cook': 'No-Cook',
  'Smoker': 'Smoker',
};

// =============================================================================
// DIETARY TAGS (optional, multi-select)
// =============================================================================

export const DIETARY_TAGS: DietaryTag[] = [
  'Vegan',
  'Vegetarian',
  'Gluten-Free',
  'Dairy-Free',
  'Keto',
  'Low-Carb',
  'Paleo',
];

export const DIETARY_TAG_LABELS: Record<DietaryTag, string> = {
  'Vegan': 'Vegan',
  'Vegetarian': 'Vegetarian',
  'Gluten-Free': 'Gluten-Free',
  'Dairy-Free': 'Dairy-Free',
  'Keto': 'Keto',
  'Low-Carb': 'Low-Carb',
  'Paleo': 'Paleo',
};

// =============================================================================
// RECIPE ATTRIBUTES (optional, multi-select)
// =============================================================================

export const RECIPE_ATTRIBUTES: RecipeAttribute[] = [
  'Quick & Easy',
  'Comfort Food',
  'Meal Prep',
  'Budget-Friendly',
  'Kid-Friendly',
  'Date Night',
  'Holiday',
  'One-Pot',
  'Crowd Pleaser',
];

export const RECIPE_ATTRIBUTE_LABELS: Record<RecipeAttribute, string> = {
  'Quick & Easy': 'Quick & Easy',
  'Comfort Food': 'Comfort Food',
  'Meal Prep': 'Meal Prep Friendly',
  'Budget-Friendly': 'Budget-Friendly',
  'Kid-Friendly': 'Kid-Friendly',
  'Date Night': 'Date Night',
  'Holiday': 'Holiday',
  'One-Pot': 'One-Pot',
  'Crowd Pleaser': 'Crowd Pleaser',
};

// =============================================================================
// URL SLUG UTILITIES
// =============================================================================

/**
 * Convert a facet value to a URL-safe slug
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Convert a URL slug back to the original facet value
 */
export function unslugify(slug: string, facetValues: string[]): string | undefined {
  return facetValues.find((value) => slugify(value) === slug);
}

// Slug lookup maps for reverse lookups
export const CATEGORY_FROM_SLUG: Record<string, RecipeCategory> = Object.fromEntries(
  CATEGORIES.map((cat) => [slugify(cat), cat])
) as Record<string, RecipeCategory>;

export const CUISINE_FROM_SLUG: Record<string, RecipeCuisine> = Object.fromEntries(
  CUISINES.map((cuisine) => [slugify(cuisine), cuisine])
) as Record<string, RecipeCuisine>;

export const COOKING_METHOD_FROM_SLUG: Record<string, CookingMethod> = Object.fromEntries(
  COOKING_METHODS.map((method) => [slugify(method), method])
) as Record<string, CookingMethod>;

export const DIETARY_TAG_FROM_SLUG: Record<string, DietaryTag> = Object.fromEntries(
  DIETARY_TAGS.map((tag) => [slugify(tag), tag])
) as Record<string, DietaryTag>;

export const RECIPE_ATTRIBUTE_FROM_SLUG: Record<string, RecipeAttribute> = Object.fromEntries(
  RECIPE_ATTRIBUTES.map((attr) => [slugify(attr), attr])
) as Record<string, RecipeAttribute>;

// =============================================================================
// FACET TYPE DEFINITIONS
// =============================================================================

export type FacetType = 'category' | 'cuisine' | 'method' | 'diet' | 'collection';

export interface FacetConfig {
  type: FacetType;
  label: string;
  pluralLabel: string;
  urlSegment: string;
  values: readonly string[];
  fromSlug: Record<string, string>;
}

export const FACET_CONFIGS: Record<FacetType, FacetConfig> = {
  category: {
    type: 'category',
    label: 'Category',
    pluralLabel: 'Categories',
    urlSegment: 'category',
    values: CATEGORIES,
    fromSlug: CATEGORY_FROM_SLUG,
  },
  cuisine: {
    type: 'cuisine',
    label: 'Cuisine',
    pluralLabel: 'Cuisines',
    urlSegment: 'cuisine',
    values: CUISINES,
    fromSlug: CUISINE_FROM_SLUG,
  },
  method: {
    type: 'method',
    label: 'Cooking Method',
    pluralLabel: 'Cooking Methods',
    urlSegment: 'method',
    values: COOKING_METHODS,
    fromSlug: COOKING_METHOD_FROM_SLUG,
  },
  diet: {
    type: 'diet',
    label: 'Dietary',
    pluralLabel: 'Dietary Options',
    urlSegment: 'diet',
    values: DIETARY_TAGS,
    fromSlug: DIETARY_TAG_FROM_SLUG,
  },
  collection: {
    type: 'collection',
    label: 'Collection',
    pluralLabel: 'Collections',
    urlSegment: 'collection',
    values: RECIPE_ATTRIBUTES,
    fromSlug: RECIPE_ATTRIBUTE_FROM_SLUG,
  },
};
